import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LockKey } from "@phosphor-icons/react";

/**
 * "Enable MFA" flow: kick off /setup → show QR → verify 6-digit code.
 * Callers pass `onEnabled` which is fired after a successful /verify.
 */
export default function MfaEnableFlow({ onEnabled }) {
  const [pending, setPending] = useState(null); // { secret, otpauth_uri } after /setup
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setPending(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code });
      toast.success("MFA enabled");
      setPending(null);
      setCode("");
      onEnabled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  if (!pending) {
    return (
      <button onClick={startSetup} disabled={busy} data-testid="mfa-setup-btn"
        className="mt-4 bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">
        <LockKey size={12} className="inline mr-1" /> Enable MFA
      </button>
    );
  }

  return (
    <div className="mt-4 border border-black p-4 grid grid-cols-2 gap-4" data-testid="mfa-setup-panel">
      <div>
        <div className="text-xs mb-2">
          Scan this QR code in Authy, 1Password, Google Authenticator, or another TOTP app.
          Then enter the 6-digit code to confirm.
        </div>
        <div className="border border-black p-3 inline-block bg-white">
          <QRCodeSVG value={pending.otpauth_uri} size={168} />
        </div>
        <details className="mt-2 text-[11px] font-mono">
          <summary className="cursor-pointer">Show secret manually</summary>
          <div className="mt-1 break-all bg-zinc-100 p-2 border border-zinc-300">{pending.secret}</div>
        </details>
      </div>
      <form onSubmit={verify} className="space-y-3">
        <div>
          <label className="label-mono">6-digit code</label>
          <input data-testid="mfa-verify-code" required inputMode="numeric" pattern="[0-9]{6}"
            maxLength={6} autoFocus
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full border border-black px-3 py-2 mt-1 font-mono text-2xl tracking-widest text-center" />
        </div>
        <button type="submit" disabled={busy} data-testid="mfa-verify-submit"
          className="w-full bg-black text-white border border-black py-2 uppercase text-xs tracking-widest font-bold">
          {busy ? "Verifying…" : "Confirm & enable"}
        </button>
        <button type="button" onClick={() => setPending(null)}
          className="w-full border border-black py-2 uppercase text-xs tracking-widest">Cancel</button>
      </form>
    </div>
  );
}
